import React, { useEffect, useState } from 'react';
import { useAuth } from '../../context/AuthContext';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { toast } from 'sonner';
import { Copy, Loader2, AlertTriangle, Zap, Eye, EyeOff, Receipt, CheckCircle2, Clock, XCircle } from 'lucide-react';
import { ACTIONS, actionsFor, statusOf, formatINR, fmtDateTime, maskAcct, copyPayoutDetails, copyText } from './payoutUtils';
import { SettlementModal } from './SettlementModal';
import { SettlementDetails, ReceiptDialog } from './SettlementDetails';

const Section = ({ title, children, testId }) => (
  <section data-testid={testId}>
    <p className="text-[11px] uppercase tracking-wider text-muted-foreground mb-1.5">{title}</p>
    <div className="grid sm:grid-cols-2 gap-x-6">{children}</div>
  </section>
);
const Row = ({ k, v, mono, testId, span }) => (
  <div className={`flex items-start justify-between gap-3 py-1.5 border-b border-border/40 text-sm ${span ? 'sm:col-span-2' : ''}`} data-testid={testId}>
    <span className="text-muted-foreground shrink-0">{k}</span>
    <span className={`text-right font-medium break-all ${mono ? 'font-mono' : ''}`}>{v ?? '—'}</span>
  </div>
);
const toneCls = { success: 'bg-green-600 hover:bg-green-700 text-white border-0', danger: 'bg-red-600 hover:bg-red-700 text-white border-0', primary: '' };

// Centered "Payout Details" modal — always loads the latest payout + settlement data from the backend.
export const PayoutDetailsModal = ({ payoutId, open, onOpenChange, onChanged }) => {
  const { api } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [action, setAction] = useState(null);
  const [form, setForm] = useState({ reason: '', utr: '', bank_reference: '', remarks: '' });
  const [busy, setBusy] = useState(false);
  const [settle, setSettle] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [fullAcct, setFullAcct] = useState(null);
  const [settlement, setSettlement] = useState(null);
  const [receipt, setReceipt] = useState(null);

  const load = async () => { const r = await api.get(`/payouts/admin/${payoutId}`); setData(r.data); setRefreshKey(k => k + 1); return r.data; };
  useEffect(() => {
    if (!open || !payoutId) return;
    setLoading(true); setData(null); setFullAcct(null); setSettlement(null);
    load().catch(() => toast.error('Failed to load payout')).finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, payoutId]);

  const p = data?.payout;
  const m = data?.merchant;
  const actors = data?.actors || {};
  const st = p ? statusOf(p.status) : null;

  const revealAcct = async () => {
    if (fullAcct) return setFullAcct(null);
    try { const r = await api.post('/payouts/admin/copy-details', { payoutIds: [p.id] }); setFullAcct(r.data.items?.[0]?.account_number || '—'); }
    catch { toast.error('Unable to load account number'); }
  };
  const copyOne = async (t) => (await copyText(t)) ? toast.success('Copied successfully') : toast.error('Unable to copy. Please try again.');

  const run = async () => {
    if (!p || !action) return;
    const a = ACTIONS[action];
    if (a.needsReason && !form.reason.trim()) return toast.error('Reason is required');
    setBusy(true);
    try {
      const ep = { approve: 'approve', reject: 'reject', processing: 'processing', failed: 'failed', execute: 'execute', retry: 'execute' }[action];
      let payload = {};
      if (action === 'approve') payload = { remarks: form.remarks || null };
      if (action === 'reject' || action === 'failed') payload = { reason: form.reason };
      if (action === 'cancel') {
        const r = await api.post('/payouts/admin/bulk-action', { payoutIds: [p.id], action: 'cancel', reason: form.reason });
        if (r.data.processed !== 1) throw new Error(r.data.results?.[0]?.reason || 'Cancel failed');
      } else {
        const r = await api.post(`/payouts/admin/${p.id}/${ep}`, payload);
        if (ep === 'execute') r.data.status === 'paid' ? toast.success(`Executed on ${r.data.rail} • UTR ${r.data.utr}`) : toast.error(`Execution ended as ${r.data.status}`);
      }
      if (ep !== 'execute') toast.success(`Payout ${a.label.toLowerCase()} successful`);
      setAction(null); setForm({ reason: '', utr: '', bank_reference: '', remarks: '' });
      await load(); onChanged?.();
    } catch (e) { toast.error(e.response?.data?.detail || e.message || 'Action failed'); }
    finally { setBusy(false); }
  };

  const subStatus = p && (
    p.status === 'paid' ? { text: settlement?.settlement_status === 'completed' || !settlement ? 'Settlement Verified' : 'Completed', Icon: CheckCircle2, cls: 'text-green-600' }
      : p.status === 'processing' || p.status === 'approved' ? { text: settlement?.proofs?.length ? 'Settlement proof submitted — pending validation' : 'Settlement proof pending', Icon: Clock, cls: 'text-amber-600' }
        : ['rejected', 'failed', 'cancelled'].includes(p.status) && p.rejection_reason ? { text: `Reason: ${p.rejection_reason}`, Icon: XCircle, cls: 'text-red-600' } : null);
  const latestSettlement = settlement?.settlements?.length ? settlement.settlements[settlement.settlements.length - 1] : null;
  const hasProcessing = p && (p.fee != null || p.gst != null || p.total_debit != null || p.utr || p.approved_by || p.processed_by || p.routed_rail_name);

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-[720px] w-[calc(100vw-1rem)] sm:w-full max-h-[92vh] overflow-y-auto rounded-lg p-0 gap-0 shadow-2xl" data-testid="payout-modal">
          <DialogHeader className="px-6 pt-5 pb-4 border-b border-border/60 space-y-1 text-left">
            <DialogTitle className="font-heading text-lg">Payout Details</DialogTitle>
            <DialogDescription asChild>
              <div className="flex flex-wrap items-center gap-3">
                <span className="font-mono text-base font-semibold text-foreground" data-testid="modal-order-id">{p?.order_id || '…'}</span>
                {st && <span className={`inline-flex items-center gap-1.5 text-xs px-2 py-0.5 rounded-sm border uppercase tracking-wide ${st.cls}`} data-testid="modal-status-badge"><span className={`w-1.5 h-1.5 rounded-full ${st.dot}`} />{st.label}</span>}
                {subStatus && <span className={`inline-flex items-center gap-1 text-xs ${subStatus.cls}`} data-testid="modal-sub-status"><subStatus.Icon className="h-3.5 w-3.5" />{subStatus.text}</span>}
              </div>
            </DialogDescription>
          </DialogHeader>

          {loading && <div className="flex items-center justify-center py-16 text-muted-foreground"><Loader2 className="h-5 w-5 animate-spin mr-2" />Loading…</div>}
          {p && (
            <div className="px-6 py-4 space-y-5">
              {p.is_flagged && p.risk_flags?.length > 0 && (
                <div className="p-2.5 rounded-sm border border-amber-500/40 bg-amber-500/5 text-xs flex items-start gap-2" data-testid="modal-risk-flags"><AlertTriangle className="h-4 w-4 text-amber-500 shrink-0" /><span>Risk flags: {p.risk_flags.map(f => f.replace(/_/g, ' ')).join(', ')}</span></div>
              )}
              <Section title="Order Details" testId="modal-order-details">
                <Row k="Order ID" v={<span className="inline-flex items-center gap-1">{p.order_id}<button onClick={() => copyOne(p.order_id)} className="text-muted-foreground hover:text-foreground" data-testid="copy-order-id"><Copy className="h-3 w-3" /></button></span>} mono />
                <Row k="Merchant" v={m?.business_name} />
                <Row k="MID" v={m?.mid} mono />
                <Row k="Reference" v={p.merchant_reference} mono />
                <Row k="Amount" v={formatINR(p.amount, 2)} mono testId="modal-amount" />
                <Row k="Created" v={fmtDateTime(p.created_at)} />
                <Row k="Status" v={st.label} />
              </Section>
              <Section title="Beneficiary" testId="modal-beneficiary">
                <Row k="Account Holder" v={p.beneficiary_name} />
                <Row k="Bank Name" v={p.bank_name} />
                <Row k="Account Number" mono testId="modal-account" v={
                  <span className="inline-flex items-center gap-1.5">{fullAcct || maskAcct(p.account_number_masked)}
                    <button onClick={revealAcct} className="text-muted-foreground hover:text-foreground" title={fullAcct ? 'Hide' : 'Show (admin)'} data-testid="toggle-account-btn">{fullAcct ? <EyeOff className="h-3 w-3" /> : <Eye className="h-3 w-3" />}</button>
                    {fullAcct && <button onClick={() => copyOne(fullAcct)} className="text-muted-foreground hover:text-foreground" data-testid="copy-account-btn"><Copy className="h-3 w-3" /></button>}
                  </span>} />
                <Row k="IFSC" v={p.ifsc_code} mono />
              </Section>
              {hasProcessing && (
                <Section title="Processing Details" testId="modal-processing">
                  {p.fee != null && <Row k="Fee" v={formatINR(p.fee, 2)} mono />}
                  {p.gst != null && <Row k="GST" v={formatINR(p.gst, 2)} mono />}
                  {p.total_debit != null && <Row k="Total Debit" v={formatINR(p.total_debit, 2)} mono testId="modal-total-debit" />}
                  {p.utr && !p.utr.startsWith('PROOF-') && <Row k="UTR" v={p.utr} mono />}
                  {p.routed_rail_name && <Row k="Rail" v={p.routed_rail_name} />}
                  {p.approved_by && <Row k="Approved By" v={actors[p.approved_by] || 'Admin'} />}
                  {p.approved_at && <Row k="Approved At" v={fmtDateTime(p.approved_at)} />}
                  {p.processed_by && <Row k="Processed By" v={actors[p.processed_by] || 'Admin'} />}
                  {p.paid_at && <Row k="Completed At" v={fmtDateTime(p.paid_at)} />}
                </Section>
              )}

              <Button variant="outline" className="w-full rounded-sm" onClick={() => copyPayoutDetails(api, [p.id], { success: () => toast.success('Copied successfully'), error: toast.error })} data-testid="modal-copy-details-btn"><Copy className="h-4 w-4 mr-2" />Copy Details</Button>

              <SettlementDetails payoutId={p.id} refreshKey={refreshKey} onLoaded={setSettlement} />

              {data.timeline?.length > 1 && (
                <section data-testid="modal-timeline">
                  <p className="text-[11px] uppercase tracking-wider text-muted-foreground mb-2">Timeline</p>
                  <ol className="relative border-l border-border/60 ml-1.5 space-y-2.5">
                    {data.timeline.map((t, i) => (
                      <li key={i} className="ml-4 text-xs" data-testid={`timeline-${t.event.toLowerCase().replace(/\s+/g, '-')}`}>
                        <span className={`absolute -left-[5px] mt-1 w-2.5 h-2.5 rounded-full ${['Rejected', 'Failed', 'Cancelled'].includes(t.event) ? 'bg-red-500' : ['Completed', 'Settlement Verified'].includes(t.event) ? 'bg-green-500' : 'bg-primary'}`} />
                        <p className="font-medium text-sm">{t.event}</p>
                        <p className="text-muted-foreground">{fmtDateTime(t.at)}{t.by ? ` · By ${t.by}` : ''}</p>
                        {t.note && <p className="text-muted-foreground">{t.note}</p>}
                      </li>
                    ))}
                  </ol>
                </section>
              )}
            </div>
          )}

          {p && (
            <DialogFooter className="px-6 py-4 border-t border-border/60 flex-row flex-wrap gap-2 sm:justify-between" data-testid="modal-actions">
              <div className="flex flex-wrap gap-2">
                {actionsFor(p.status).map(k => (
                  <Button key={k} size="sm" variant={ACTIONS[k].tone === 'primary' ? 'default' : 'outline'} className={`rounded-sm ${toneCls[ACTIONS[k].tone]}`} onClick={() => k === 'paid' ? setSettle(true) : setAction(k)} data-testid={`drawer-action-${k}`}>
                    {k === 'execute' && <Zap className="h-3.5 w-3.5 mr-1" />}{ACTIONS[k].label}
                  </Button>
                ))}
                {p.status === 'paid' && latestSettlement && <Button size="sm" variant="outline" className="rounded-sm" onClick={() => setReceipt(latestSettlement.id)} data-testid="modal-view-receipt-btn"><Receipt className="h-3.5 w-3.5 mr-1" />View Receipt</Button>}
              </div>
              <Button size="sm" variant="ghost" className="rounded-sm" onClick={() => onOpenChange(false)} data-testid="modal-close-btn">Close</Button>
            </DialogFooter>
          )}
        </DialogContent>
      </Dialog>

      <SettlementModal payout={p ? { ...p, merchant: m } : null} open={settle} onOpenChange={setSettle} onCompleted={async () => { await load(); onChanged?.(); }} />
      <ReceiptDialog settlementId={receipt} open={!!receipt} onOpenChange={(o) => !o && setReceipt(null)} />

      <Dialog open={!!action} onOpenChange={(o) => !o && setAction(null)}>
        <DialogContent className="rounded-sm" data-testid="drawer-action-dialog">
          <DialogHeader>
            <DialogTitle>{action && ACTIONS[action].label} — {p?.order_id}</DialogTitle>
            <DialogDescription>{p && `${formatINR(p.amount)} to ${p.beneficiary_name}. ${action === 'cancel' ? 'Held funds will be released to the merchant wallet per the existing ledger rules.' : 'This uses the standard payout workflow.'}`}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3">
            {action && ACTIONS[action].needsReason && <div className="space-y-1.5"><Label>Reason *</Label><Textarea rows={3} value={form.reason} onChange={e => setForm(f => ({ ...f, reason: e.target.value }))} className="rounded-sm" data-testid="action-reason-input" /></div>}
            {action === 'approve' && <div className="space-y-1.5"><Label>Remarks</Label><Input value={form.remarks} onChange={e => setForm(f => ({ ...f, remarks: e.target.value }))} className="rounded-sm" /></div>}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAction(null)} className="rounded-sm">Cancel</Button>
            <Button onClick={run} disabled={busy} className={`rounded-sm ${action ? toneCls[ACTIONS[action].tone] : ''}`} data-testid="action-confirm-btn">{busy && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}Confirm {action && ACTIONS[action].label}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
